/**
 * Builds day/city cards for TripDetail — mirrors iOS CityVisitCard list ordering.
 */

import type { ItineraryItemType, Trip, TripCityVisitMeta } from '../types/client';
import { isAtSeaLocation, parsePortTimeParts } from '../services/cruiseItinerariesApi';

export interface TripDayCardModel {
  cityId: string;
  cityName: string;
  arrivalDate: string | null;
  departureDate: string | null;
  portArrivalHm: string | null;
  portDepartureHm: string | null;
  isAtSea: boolean;
  isCruisePort: boolean;
  cruisePortStatus: TripCityVisitMeta['cruisePortStatus'];
  itemTypes: ItineraryItemType[];
  countryISO2: string | null;
  countryName: string | null;
  currencyCode: string | null;
  timeZoneIdentifier: string | null;
  dockNumber: string | null;
}

function dateOnlyFromUnknown(value: unknown): string | null {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (ymd) return `${ymd[1]}-${ymd[2]}-${ymd[3]}`;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, '0');
  const d = String(parsed.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function hmFromUnknown(value: unknown): string | null {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const hm = /^(\d{1,2}):(\d{2})/.exec(raw);
  if (hm) return `${hm[1].padStart(2, '0')}:${hm[2]}`;
  const iso = /T(\d{2}):(\d{2})/.exec(raw);
  if (iso) return `${iso[1]}:${iso[2]}`;
  return null;
}

export function parseCityVisitMetaFromDevice(body: Record<string, unknown>): TripCityVisitMeta {
  const arrivalDate = dateOnlyFromUnknown(body.arrivalDate);
  const departureDate = dateOnlyFromUnknown(body.departureDate) ?? arrivalDate;
  let portArrivalHm =
    hmFromUnknown(body.portArrivalHm) ?? hmFromUnknown(body.portArrivalTime);
  let portDepartureHm =
    hmFromUnknown(body.portDepartureHm) ?? hmFromUnknown(body.portDepartureTime);
  if (!portArrivalHm && !portDepartureHm && typeof body.time === 'string') {
    const parts = parsePortTimeParts(body.time);
    portArrivalHm = parts.arrivalHm;
    portDepartureHm = parts.departureHm;
  }
  const statusRaw = body.cruisePortStatus != null ? String(body.cruisePortStatus).toLowerCase() : '';
  const cruisePortStatus: TripCityVisitMeta['cruisePortStatus'] =
    statusRaw === 'docked' ||
    statusRaw === 'tendered' ||
    statusRaw === 'cruising' ||
    statusRaw === 'unknown'
      ? statusRaw
      : null;

  const aiJobId =
    body.aiJobId != null && String(body.aiJobId).trim()
      ? String(body.aiJobId).trim()
      : null;
  const aiJobStartedAt =
    body.aiJobStartedAt != null && String(body.aiJobStartedAt).trim()
      ? String(body.aiJobStartedAt)
      : null;
  const aiStatus =
    body.aiStatus != null && String(body.aiStatus).trim()
      ? String(body.aiStatus).trim()
      : null;
  const aiUpdatedAt =
    body.aiUpdatedAt != null && String(body.aiUpdatedAt).trim()
      ? String(body.aiUpdatedAt)
      : null;

  return {
    arrivalDate,
    departureDate,
    portArrivalHm,
    portDepartureHm,
    cruisePortStatus,
    dockNumber:
      body.dockNumber != null && String(body.dockNumber).trim()
        ? String(body.dockNumber).trim()
        : null,
    isEmbarkation: Boolean(body.isEmbarkation),
    isDebarkation: Boolean(body.isDebarkation),
    countryISO2:
      body.countryISO2 != null && String(body.countryISO2).trim()
        ? String(body.countryISO2).trim().toUpperCase()
        : null,
    countryName:
      body.countryName != null && String(body.countryName).trim()
        ? String(body.countryName).trim()
        : null,
    currencyCode:
      body.currencyCode != null && String(body.currencyCode).trim()
        ? String(body.currencyCode).trim().toUpperCase()
        : null,
    timeZoneIdentifier:
      body.timeZoneIdentifier != null && String(body.timeZoneIdentifier).trim()
        ? String(body.timeZoneIdentifier).trim()
        : null,
    aiJobId,
    aiJobStartedAt,
    aiStatus,
    aiUpdatedAt,
  };
}

function addDaysIso(iso: string, days: number): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  date.setDate(date.getDate() + days);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function daysBetweenInclusive(start: string, end: string): string[] {
  const result: string[] = [];
  let cur = start;
  let guard = 0;
  while (cur <= end && guard < 400) {
    result.push(cur);
    cur = addDaysIso(cur, 1);
    guard += 1;
  }
  return result;
}

function itemTypesForCity(trip: Trip, cityId: string, date: string | null): ItineraryItemType[] {
  const days = trip.itineraryDays ?? [];
  const types = new Set<ItineraryItemType>();
  for (const day of days) {
    if (day.cityId && day.cityId !== cityId) continue;
    if (date && day.date !== date && day.cityId !== cityId) continue;
    if (date && day.cityId === cityId && day.date !== date) {
      // still include if same city — multi-day visits
    }
    if (date && !day.cityId && day.date !== date) continue;
    for (const item of day.items ?? []) {
      types.add(item.type);
    }
  }
  return Array.from(types);
}

/** Ordered city visit cards for TripDetail. */
export function buildTripDayCards(trip: Trip): TripDayCardModel[] {
  const cityIds = trip.cityIds ?? [];
  if (cityIds.length === 0) return [];

  const isCruise = trip.tripType === 'cruise';
  const metaMap = trip.cityVisitMeta ?? {};
  const dateSpan =
    trip.startDate && trip.endDate ? daysBetweenInclusive(trip.startDate, trip.endDate) : [];

  return cityIds.map((cityId, index) => {
    const normalizedId = cityId.trim();
    const cityName =
      trip.cityNames?.[normalizedId]?.trim() ||
      Object.entries(trip.cityNames ?? {}).find(
        ([id, name]) => id.toLowerCase() === normalizedId.toLowerCase() && name.trim()
      )?.[1]?.trim() ||
      '';
    const displayName =
      cityName && !looksLikeUuid(cityName) ? cityName : 'Unknown city';
    const meta =
      metaMap[normalizedId] ??
      Object.entries(metaMap).find(([id]) => id.toLowerCase() === normalizedId.toLowerCase())?.[1];
    let arrivalDate = meta?.arrivalDate ?? null;
    let departureDate = meta?.departureDate ?? arrivalDate;

    // Fallback: assign sequential dates when count matches trip length (typical cruise import)
    if (!arrivalDate && dateSpan.length === cityIds.length) {
      arrivalDate = dateSpan[index] ?? null;
      departureDate = arrivalDate;
    } else if (!arrivalDate && dateSpan.length > 0 && index < dateSpan.length) {
      arrivalDate = dateSpan[index] ?? null;
      departureDate = arrivalDate;
    }

    const atSea = isAtSeaLocation(displayName);
    return {
      cityId: normalizedId,
      cityName: displayName,
      arrivalDate,
      departureDate,
      portArrivalHm: meta?.portArrivalHm ?? null,
      portDepartureHm: meta?.portDepartureHm ?? null,
      isAtSea: atSea,
      isCruisePort: isCruise && !atSea,
      cruisePortStatus: meta?.cruisePortStatus ?? (isCruise && !atSea ? 'docked' : null),
      itemTypes: itemTypesForCity(trip, normalizedId, arrivalDate),
      countryISO2: meta?.countryISO2 ?? null,
      countryName: meta?.countryName ?? null,
      currencyCode: meta?.currencyCode ?? null,
      timeZoneIdentifier: meta?.timeZoneIdentifier ?? null,
      dockNumber: meta?.dockNumber ?? null,
    };
  });
}

function looksLikeUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());
}

export function formatTripHeaderDate(iso: string | null): string | null {
  if (!iso) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!match) return iso;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatCardWeekdayDate(iso: string | null): string | null {
  if (!iso) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!match) return iso;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

export function formatPortClockHm(hm: string): string {
  const [h, m] = hm.split(':').map(Number);
  const date = new Date(2000, 0, 1, h, m);
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** Secondary time line under the date — mirrors iOS WeatherCardRow.secondaryDateLine */
export function portTimeLineForCard(card: TripDayCardModel): string | null {
  const arr = card.portArrivalHm;
  const dep = card.portDepartureHm;
  if (!arr && !dep) return null;

  const isFullDayDefault =
    arr === '00:00' && (dep === '23:59' || dep === '23:59:00');
  if (isFullDayDefault) return null;

  if (arr && dep) {
    return `${formatPortClockHm(arr)} - ${formatPortClockHm(dep)}`;
  }
  if (arr) return `Arr: ${formatPortClockHm(arr)}`;
  if (dep) return `Dep: ${formatPortClockHm(dep)}`;
  return null;
}

export function pendingOptionCount(trip: Trip): number {
  const options = trip.cityOptions ?? {};
  let count = 0;
  for (const list of Object.values(options)) {
    for (const opt of list) {
      if (!opt.completed) count += 1;
    }
  }
  return count;
}

/** Pending trip-level checklist tasks — mirrors iOS Trip.pendingTaskCount */
export function pendingTaskCount(trip: Trip): number {
  return (trip.taskItems ?? []).filter((t) => !t.isDone).length;
}

export function defaultShowPlanIcons(trip: Trip, now = new Date()): boolean {
  if (!trip.startDate) return true;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trip.startDate.trim());
  if (!match) return true;
  const start = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (now < start) return true;
  return false;
}
